import { useEffect, useState } from "react"
import { Link, Navigate } from "react-router-dom"
import { FolderOpen, Plus, Trash2, UsersRound } from "lucide-react"
import { api } from "../api/client"
import { useApp } from "../state/AppState"
import {
	Avatar,
	Button,
	Card,
	EmptyState,
	Field,
	Input,
	Modal,
	Spinner,
	cx
} from "../components/ui"
import { PersonGrid } from "../components/issues/ProjectTeams"
import { plural } from "../lib/format"

/**
 * Teams: a saved group of people, and the projects they are on.
 *
 * The group itself grants nothing. What grants access is putting the team on a
 * project, which its owner does once - from then on everyone on the team sees
 * that whole project, including the issues raised afterwards.
 *
 * Removing someone from a team takes those projects away again, but it never
 * removes them from an issue they were invited to individually, and it never
 * touches anything they have already written. That record stands.
 */
export function TeamsPage() {
	const { user, projects, toast } = useApp()
	const [teams, setTeams] = useState([])
	const [loading, setLoading] = useState(true)
	const [creating, setCreating] = useState(false)
	const [pendingDelete, setPendingDelete] = useState(null)

	const allowed = (user?.plan?.teams ?? 0) !== 0

	const load = () =>
		api
			.teams()
			.then((data) => setTeams(data.teams))
			.catch((cause) => toast(cause.message, "error"))
			.finally(() => setLoading(false))

	useEffect(() => {
		if (allowed) load()
		else setLoading(false)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [allowed])

	// Not on a plan that includes teams: straight to billing, with a marker so
	// that page can say what sent them there. `replace` keeps Back going to
	// wherever they actually came from rather than bouncing them in a loop.
	if (!allowed) return <Navigate to="/settings/billing?upsell=teams" replace />

	if (loading) return <Spinner label="Loading teams" />

	return (
		<div className="space-y-5">
			<div className="flex flex-wrap items-end justify-between gap-3">
				<div>
					<h1 className="text-xl font-semibold tracking-tight text-ink-900">Teams</h1>
					<p className="mt-0.5 max-w-2xl text-[0.8125rem] leading-relaxed text-ink-500">
						A saved group of people. Put one on a project and everyone on it sees that
						whole project — no inviting them question by question.
					</p>
				</div>
				<Button variant="primary" icon={Plus} onClick={() => setCreating(true)}>
					New team
				</Button>
			</div>

			{teams.length === 0 ? (
				<EmptyState
					icon={UsersRound}
					title="No teams yet"
					action={
						<Button variant="primary" icon={Plus} onClick={() => setCreating(true)}>
							Create a team
						</Button>
					}
				/>
			) : (
				<div className="space-y-4">
					{teams.map((team) => (
						<TeamCard
							key={team.team_id}
							team={team}
							currentUserId={user?.user_id}
							projects={projects}
							onChanged={load}
							onDelete={() => setPendingDelete(team)}
						/>
					))}
				</div>
			)}

			<TeamDialog
				open={creating}
				onClose={() => setCreating(false)}
				onSaved={async () => {
					await load()
					setCreating(false)
				}}
			/>

			<Modal
				open={Boolean(pendingDelete)}
				onClose={() => setPendingDelete(null)}
				title={`Delete "${pendingDelete?.name}"?`}
				size="sm"
				footer={
					<>
						<Button onClick={() => setPendingDelete(null)}>Cancel</Button>
						<Button
							variant="danger"
							icon={Trash2}
							onClick={async () => {
								const team = pendingDelete
								setPendingDelete(null)
								try {
									await api.deleteTeam(team.team_id)
									await load()
									toast(`Deleted "${team.name}"`, "success")
								} catch (cause) {
									toast(cause.message, "error")
								}
							}}
						>
							Delete team
						</Button>
					</>
				}
			>
				<p className="text-[0.8125rem] leading-relaxed text-ink-600">
					The team comes off every project it is on, so its people stop seeing those
					projects. Anyone invited to an individual issue stays on it, and nothing
					anybody has already written moves.
				</p>
			</Modal>
		</div>
	)
}

/* -------------------------------------------------------------------------- */

/**
 * One team, as a page section rather than a row of chips.
 *
 * Name, job title and company for every person, because that is what tells you
 * whose answer you are about to read. A first name and an avatar does not,
 * once half the people on a job work for someone else.
 */
function TeamCard({ team, currentUserId, projects, onChanged, onDelete }) {
	const { toast } = useApp()
	const [editing, setEditing] = useState(false)

	async function run(work) {
		try {
			await work()
			await onChanged()
		} catch (cause) {
			toast(cause.message, "error")
		}
	}

	const onProjects = (team.project_ids || [])
		.map((id) => projects.find((project) => project.project_id === id))
		.filter(Boolean)

	return (
		<Card className="overflow-hidden">
			<div className="flex flex-wrap items-start justify-between gap-3 border-b border-ink-100 px-4 py-3">
				<div className="min-w-0">
					<h2 className="flex items-center gap-2 text-[0.9375rem] font-semibold text-ink-900">
						<span className="grid h-6 w-6 place-items-center rounded-md bg-brand-100 text-brand-800">
							<UsersRound size={13} aria-hidden />
						</span>
						{team.name}
					</h2>
					<p className="mt-1 text-2xs text-ink-500">
						{plural(team.size, "person", "people")}
						{team.pending_emails.length > 0 &&
							` · ${team.pending_emails.length} not signed up yet`}
					</p>
				</div>
				<div className="flex items-center gap-2">
					<Button size="sm" onClick={() => setEditing(true)}>
						Add people
					</Button>
					<Button size="sm" variant="ghost" icon={Trash2} onClick={onDelete}>
						Delete
					</Button>
				</div>
			</div>

			<PersonGrid
				members={team.members}
				pendingEmails={team.pending_emails}
				currentUserId={currentUserId}
				lockedId={team.owner_id}
				onRemoveMember={(member) =>
					run(() => api.removeTeamMember(team.team_id, member.user_id))
				}
				onRemovePending={(address) =>
					run(() => api.removeTeamPending(team.team_id, address))
				}
			/>

			<div className="flex flex-wrap items-center gap-2 border-t border-ink-100 bg-ink-50/70 px-4 py-2.5">
				<FolderOpen size={13} className="shrink-0 text-ink-400" aria-hidden />
				{onProjects.length === 0 ? (
					<p className="text-2xs text-ink-500">
						Not on a project yet — open a project and use{" "}
						<span className="font-medium text-ink-700">Invite a team</span> to give
						everyone here access to all of it.
					</p>
				) : (
					<>
						<span className="text-2xs text-ink-500">Sees every issue in</span>
						{onProjects.map((project) => (
							<Link
								key={project.project_id}
								to={`/projects/${project.project_id}/team`}
								className="rounded-full bg-white px-2 py-0.5 text-2xs font-medium text-ink-700 ring-1 ring-inset ring-ink-200 transition-colors hover:text-brand-800 hover:ring-brand-300"
							>
								{project.name}
							</Link>
						))}
					</>
				)}
			</div>

			<TeamDialog
				open={editing}
				team={team}
				onClose={() => setEditing(false)}
				onSaved={async () => {
					await onChanged()
					setEditing(false)
				}}
			/>
		</Card>
	)
}

/* -------------------------------------------------------------------------- */

function TeamDialog({ open, team, onClose, onSaved }) {
	const { people, user } = useApp()
	const [name, setName] = useState("")
	const [emails, setEmails] = useState("")
	const [selected, setSelected] = useState([])
	const [busy, setBusy] = useState(false)
	const [error, setError] = useState(null)

	useEffect(() => {
		if (!open) return
		setName(team?.name || "")
		setEmails("")
		setSelected(team ? team.members.map((m) => m.user_id) : [user?.user_id].filter(Boolean))
		setError(null)
	}, [open, team, user])

	const parsedEmails = emails
		.split(/[\s,;]+/)
		.map((value) => value.trim())
		.filter(Boolean)

	async function save() {
		setBusy(true)
		setError(null)
		try {
			const payload = {
				name: name.trim(),
				user_ids: selected,
				emails: [...(team?.pending_emails || []), ...parsedEmails]
			}
			if (team) await api.updateTeam(team.team_id, payload)
			else await api.createTeam(payload)
			await onSaved()
		} catch (cause) {
			setError(cause.message)
		} finally {
			setBusy(false)
		}
	}

	return (
		<Modal
			open={open}
			onClose={onClose}
			title={team ? `Edit ${team.name}` : "New team"}
			description="Put this group on a project and every one of them sees all of it."
			footer={
				<>
					<Button onClick={onClose}>Cancel</Button>
					<Button
						variant="primary"
						onClick={save}
						busy={busy}
						disabled={busy || !name.trim()}
					>
						{team ? "Save team" : "Create team"}
					</Button>
				</>
			}
		>
			<div className="space-y-4">
				<Field label="Team name" error={error}>
					<Input
						value={name}
						onChange={(event) => setName(event.target.value)}
						placeholder="Design consultants"
						maxLength={60}
						autoFocus
					/>
				</Field>

				{people.length > 0 && (
					<div>
						<p className="mb-1.5 text-[0.8125rem] font-medium text-ink-700">
							People you already work with
						</p>
						<div className="max-h-48 space-y-0.5 overflow-y-auto scrollbar-slim">
							{people.map((person) => {
								const on = selected.includes(person.user_id)
								const isSelf = person.user_id === user?.user_id
								return (
									<button
										key={person.user_id}
										type="button"
										disabled={isSelf}
										onClick={() =>
											setSelected((current) =>
												on
													? current.filter((id) => id !== person.user_id)
													: [...current, person.user_id]
											)
										}
										className={cx(
											"flex w-full items-center gap-2.5 rounded-lg px-2 py-1.5 text-left transition-colors",
											on ? "bg-brand-50" : "hover:bg-ink-100",
											isSelf && "opacity-60"
										)}
									>
										<Avatar person={person} size="sm" />
										<span className="min-w-0 flex-1">
											<span className="block truncate text-[0.8125rem] font-medium leading-tight text-ink-900">
												{isSelf ? `${person.name} (you)` : person.name}
											</span>
											<span className="block truncate text-2xs leading-tight text-ink-500">
												{[person.role, person.company].filter(Boolean).join(", ") ||
													person.email}
											</span>
										</span>
										{on && (
											<span
												className="h-2 w-2 shrink-0 rounded-full bg-brand-600"
												aria-hidden
											/>
										)}
									</button>
								)
							})}
						</div>
					</div>
				)}

				<Field
					label="Add by email"
					hint="Anyone without an account joins the team, and its projects, the moment they sign up."
				>
					<Input
						value={emails}
						onChange={(event) => setEmails(event.target.value)}
						placeholder="name@company.com, other@firm.co.uk"
					/>
				</Field>
			</div>
		</Modal>
	)
}
